import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useColors } from '../../../../../theme/ColorModeContext';
import { StageSection, Prose, KeyPoints, FactGrid, Callout, MiniLabel, Panel, SegmentedToggle } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';
import { CANTON_PARTIES, CantonParty, TxNode, countNodes, dvpTransaction, projectionFor } from './cantonModel';
import { PartyChip, PartyPicker } from './cantonVisuals';

export const meta = {
    title: 'Privacy by Design: Need-to-Know Visibility',
    tagline: 'Nobody sees a transaction they weren\'t part of.',
};

type ViewModel = 'canton' | 'public';

const PARTY_NOTES: Record<CantonParty, string> = {
    Alice: 'Alice is a stakeholder of the DvP agreement at the root, and a party that sees an action also sees everything that action caused. So Alice sees the whole trade, both legs included.',
    Bob: 'Same as Alice: as a stakeholder of the root agreement, Bob sees the complete trade.',
    Bank: 'Bank sees only its own leg: an Iou it issued moved from Alice to Bob. It never learns that this was payment for shares, or that Registry was involved at all.',
    Registry: 'Registry sees only the share leg: 10 ACME moved from Bob to Alice. The cash side, and Bank\'s role in it, stay invisible to it.',
    Carol: 'Carol is not an informee of any part of this transaction. Carol\'s node receives nothing: not the contents, not even the fact that a trade happened.',
};

const TreeNode = ({ node, visible, viewer, depth }: { node: TxNode; visible: Set<string>; viewer: CantonParty; depth: number }) => {
    const colors = useColors();
    const shown = visible.has(node.id);
    return (
        <Box sx={{ pl: depth === 0 ? 0 : { xs: '14px', md: '26px' }, borderLeft: depth === 0 ? 'none' : `2px solid ${colors.border.subtle}`, ml: depth === 0 ? 0 : { xs: '6px', md: '12px' } }}>
            <Box sx={{
                my: '8px',
                p: '10px 14px',
                borderRadius: '8px',
                border: `1px solid ${shown ? colors.border.strong : colors.border.subtle}`,
                backgroundColor: shown ? colors.bg.primary : 'transparent',
                backgroundImage: shown ? 'none' : `repeating-linear-gradient(135deg, ${colors.bg.tertiary} 0 8px, transparent 8px 16px)`,
                transition: 'all 0.25s ease',
            }}>
                {shown ? (
                    <>
                        <Typography sx={{ color: colors.text.primary, fontWeight: 800, fontSize: '0.92rem' }}>
                            {node.action} <Box component="span" sx={{ color: colors.text.secondary, fontWeight: 400 }}>{node.detail}</Box>
                        </Typography>
                        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mt: 0.75, alignItems: 'center' }}>
                            <Typography component="span" sx={{ color: colors.text.muted, fontSize: '0.74rem' }}>informees</Typography>
                            {node.informees.map((party) => <PartyChip key={party} party={party} />)}
                        </Box>
                    </>
                ) : (
                    <Typography sx={{ color: colors.text.muted, fontSize: '0.85rem', fontFamily: '"Share Tech Mono", monospace' }}>
                        never delivered to {viewer}
                    </Typography>
                )}
            </Box>
            {node.children.map((child) => <TreeNode key={child.id} node={child} visible={visible} viewer={viewer} depth={depth + 1} />)}
        </Box>
    );
};

const allIds = (node: TxNode): string[] => [node.id, ...node.children.flatMap(allIds)];

const VisibilityExplorer = () => {
    const colors = useColors();
    const [model, setModel] = useState<ViewModel>('canton');
    const [viewer, setViewer] = useState<CantonParty>('Bank');
    const visible = model === 'public' ? new Set(allIds(dvpTransaction)) : projectionFor(dvpTransaction, viewer);
    const total = countNodes(dvpTransaction);

    return (
        <Panel>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <PartyPicker label="View the transaction as" parties={CANTON_PARTIES} selected={[viewer]} onToggle={setViewer} />
                <SegmentedToggle<ViewModel>
                    ariaLabel="Visibility model"
                    value={model}
                    onChange={setModel}
                    options={[{ value: 'canton', label: 'Canton' }, { value: 'public', label: 'Public chain' }]}
                />
            </Box>
            <MiniLabel>What {viewer}'s node receives</MiniLabel>
            <TreeNode node={dvpTransaction} visible={visible} viewer={viewer} depth={0} />
            <Box sx={{ mt: 2, p: '14px', borderRadius: '8px', border: `1px solid ${colors.border.subtle}`, backgroundColor: colors.bg.secondary }} aria-live="polite">
                <Typography sx={{ color: colors.text.primary, fontWeight: 800, mb: 0.5 }}>
                    {viewer} sees {visible.size} of {total} actions
                </Typography>
                <Typography sx={{ color: colors.text.secondary, fontSize: '0.9rem', lineHeight: 1.65 }}>
                    {model === 'public'
                        ? `On a public chain the whole transaction is broadcast to every node, so ${viewer} sees everything, whether involved or not. So does every competitor, analyst and bot watching the chain.`
                        : PARTY_NOTES[viewer]}
                </Typography>
            </Box>
        </Panel>
    );
};

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - what "need-to-know" visibility means in practice
//   Q1 - how this contrasts with the "every full node re-executes everything" model
//   Q2 - privacy doesn't mean transactions go unverified
export const blocks: StageBlock[] = [
    content('intro', () => (
        <StageSection gradientBorder eyebrow="Stage 3 of 8" title="Only the involved parties ever see it">
            <Prose>
                On a public chain, every validator receives and can inspect every transaction, forever. Canton
                inverts that by design: a participant node only ever receives data about contracts involving the
                parties it hosts. If a contract only concerns Bank A and Bank B, Bank C's infrastructure never
                receives that transaction's contents at all. Not "receives it encrypted", not "sees it but ignores
                it": the data simply never arrives.
            </Prose>
            <Prose>
                Who counts as "involved" is decided by the contracts themselves. The previous stage introduced
                stakeholders (signatories and observers). For each action in a transaction, the ledger works out its{' '}
                <strong>informees</strong>: the stakeholders of a contract being created or consumed, plus whoever is
                exercising a choice on it (actions that merely read a contract inform only its signatories and the
                actor). Those parties, and only those, are told about that action.
            </Prose>
        </StageSection>
    )),

    content('sub-transaction', () => (
        <StageSection title="Privacy inside a single transaction">
            <Prose>
                Canton goes further than hiding whole transactions: it hides <em>parts</em> of them. A transaction is
                really a tree of actions, where exercising a choice causes further creates and exercises underneath
                it. Each party gets its own <strong>projection</strong> of that tree: the actions it's an informee
                of, together with everything those actions caused. This is called{' '}
                <strong>sub-transaction privacy</strong>.
            </Prose>
            <Prose>
                Below is a delivery-versus-payment (DvP) trade: Alice pays Bob $100 of Bank-issued cash in exchange
                for 10 ACME shares recorded by Registry, all in one transaction. Switch between parties to see what
                each one's node actually receives, then flip to the public-chain model to compare.
            </Prose>
            <VisibilityExplorer />
        </StageSection>
    )),

    question(0),

    content('contrast', () => (
        <StageSection title="A deliberate departure from full public re-execution">
            <Prose>
                The Bitcoin and Ethereum tracks describe full nodes independently re-executing every transaction on
                the network. That's where their trustless guarantee comes from: you don't have to believe anyone,
                because your node checked every state change itself. The price is that every node must see
                everything.
            </Prose>
            <Prose>
                Canton trades universal visibility away on purpose. Not every node re-checks every transaction,
                because not every node ever receives every transaction. Each transaction is validated by the nodes of
                the parties it affects, which are also the only ones with anything at stake in it. Digital Asset
                describes this as <strong>proof of stakeholder</strong>, in contrast with proof of work or proof of
                stake.
            </Prose>
            <FactGrid items={[
                ['Public chain: who validates?', 'Everyone. Every full node re-executes every transaction, including ones it has no interest in.'],
                ['Canton: who validates?', 'The stakeholders. Each informee\'s node re-executes the part of the transaction it can see.'],
                ['Public chain: what can you audit?', 'The entire history of the network, from your own node.'],
                ['Canton: what can you audit?', 'Everything that involves your own parties, and nothing else. There is no node that holds the whole ledger.'],
            ]} />
            <Callout tone="warning" title="Private is not the same as invisible">
                Contents are hidden, but some <strong>metadata</strong> isn't. The infrastructure that routes
                messages (Stage 5) learns which nodes are talking to each other, when, and how much data they send,
                even though it can't read any of it. For some threat models, "Bank A and Bank B exchanged a large
                message at 09:00" is sensitive in itself.
            </Callout>
        </StageSection>
    )),

    question(1),

    content('still-verified', () => (
        <StageSection title="Private doesn't mean unverified">
            <Prose>
                Limiting who sees a transaction is not the same as skipping verification of it. When a transaction
                arrives, each informee's participant node independently re-runs its own view of it through the DAML
                engine and checks three things before agreeing to it:
            </Prose>
            <FactGrid columns={3} items={[
                ['Conformance', 'The actions really are what the DAML templates allow. Nobody made up a result the code wouldn\'t produce.'],
                ['Authorization', 'Every required signatory and controller actually authorized its part of the transaction.'],
                ['Consistency', 'Every input contract is still active, so nothing is being spent twice.'],
            ]} />
            <Prose>
                The pieces each party sees are cryptographically bound together. The transaction is organized as a
                Merkle tree, so Bank's view and Registry's view provably belong to the <em>same</em> transaction,
                even though neither can read the other's part. The next stage looks at where this data is stored,
                and Stage 5 at how everyone agrees on ordering without anyone reading the contents.
            </Prose>
            <Callout tone="security" title="Security angle: privacy is configured in code">
                Visibility follows directly from the template: every party you add as an observer, or who can
                exercise a choice, receives that contract's data and possibly the data of everything the choice
                touches. A template that names too many observers, or a workflow that fetches a private contract
                inside a widely visible choice, leaks information the ledger will faithfully deliver. Canton won't
                over-share on its own, but it will do exactly what the DAML says.
            </Callout>
            <KeyPoints items={[
                <>Visibility is restricted to informees; correctness is still enforced by every informee for the part it sees.</>,
                <>Sub-transaction privacy means even parties to the same transaction can see different parts of it.</>,
                <>This is the foundation the dedicated privacy and state-integrity tracks build much deeper on.</>,
            ]} />
        </StageSection>
    )),

    question(2),
];
